import React, { useState, useEffect, useContext } from 'react';
import { createAuth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import { identify } from './helpers';
import { setGlobalIdToken } from './services/http-service';
import {
  setGlobalAuthState,
  cleanupDeprecatedTokens,
} from './helpers/auth-context';
import { UserRoles } from 'constants/user';
import R from 'helpers/ramda';

const DEFAULT_REDIRECT_CALLBACK = (appState: any) =>
  window.history.replaceState(
    appState,
    document.title,
    window.location.pathname,
  );

export const Auth0Context = React.createContext({} as any);
export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [error, setError] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
  const [user, setUser] = useState({});
  const [auth0Client, setAuth0] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [idTokenClaims, setIdTokenClaims] = useState<any>(null);

  // Helper functions for role management
  const hasRole = (role: string) => {
    if (!idTokenClaims) return false;
    return (
      R.pathOr('', ['https://quashed.co.nz/roles', 0], idTokenClaims) === role
    );
  };

  const getRole = () => {
    if (!idTokenClaims) return UserRoles.USER;
    return R.pathOr(
      UserRoles.USER,
      ['https://quashed.co.nz/roles', 0],
      idTokenClaims,
    );
  };

  const isAdviserRole = () => {
    return getRole() === UserRoles.ADVISER;
  };

  useEffect(() => {
    const initAuth0 = async () => {
      // Clean up any deprecated localStorage tokens
      cleanupDeprecatedTokens();

      const auth0FromHook = await createAuth0Client(
        initOptions as Auth0ClientOptions,
      );
      setAuth0(auth0FromHook);

      if (window.location.search.includes('code=')) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          onRedirectCallback(appState);
        } catch (ex) {
          console.error('Auth error:', ex);
          setError(true);
        }
      }

      const authenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(authenticated);

      if (authenticated) {
        const currentUser = await auth0FromHook.getUser();
        const token = await auth0FromHook.getIdTokenClaims();
        setIdTokenClaims(token || null);
        setUser(currentUser || {});

        // Set global auth state for services
        if (token) {
          setGlobalAuthState(token, auth0FromHook);

          // Cache ID token's raw JWT for API calls (API expects ID token)
          if (token.__raw) {
            setGlobalIdToken(token.__raw);
          }
        }

        if (currentUser?.sub) {
          identify(currentUser.sub, currentUser);
        }
      }

      setLoading(false);
    };
    initAuth0();
  }, []);

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        hasRole,
        getRole,
        isAdviserRole,
        authError: error,
        idTokenClaims,
        getIdTokenClaims: (...p: any[]) => auth0Client?.getIdTokenClaims(...p),
        getAccessTokenSilently: (...p: any[]) =>
          auth0Client?.getAccessTokenSilently(...p),
        loginWithRedirect: (...p: any[]) => {
          if (!error && auth0Client) {
            auth0Client.loginWithRedirect(...p);
          }
        },
        getTokenSilently: (...p: any[]) => auth0Client?.getTokenSilently(...p),
        getTokenWithPopup: (...p: any[]) =>
          auth0Client?.getTokenWithPopup(...p),
        // federated is deliberately NOT set: under the old v1 runtime this
        // logoutParams object was ignored, so users were never signed out of
        // their upstream IdP (e.g. Google). Adding federated now that the
        // option actually works would log users out of Google entirely.
        logout: () =>
          auth0Client &&
          auth0Client.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          }),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
